import React, { useCallback, useMemo } from 'react';
import {
  CategoryConfigurationDefinitionResponse,
  ConfigurationFieldType,
  FieldConfigurationRequest,
  SkuListConfigurationFlag,
  SkuType,
} from '@ydistri/api-sdk';
import ConfigurationRowTitle from '../ConfigurationRowTitle';
import { isSectionTypeSource, isSectionTypeTarget } from '../../configurationUtils';
import {
  ConfigurationFieldsWrapperCentered,
  ConfigurationFieldsWrapperTarget,
  ConfigurationRowWrapper,
} from '../ConfigurationRow';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { Row } from '@ydistri/ds';
import ConfigurationEntityListSelect from './ConfigurationEntityListSelect';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { usePatchCategoryConfigurationMutation } from '../../../apiTemplate';
import { iterableSkuTypes } from '../../../../../helpers/types/iterableEnums';
import {
  createEntityListConfig,
  getAllAvailableEntityLists,
  parseEntityListConfigs,
} from './entityListLib';
import { DefaultOptionType } from 'rc-select/lib/Select';
import {
  highlightedConfigurationFieldTypes,
  isTargetConfiguration,
} from '../segmentPotentialDefinitionLib';
import {
  getDefaultEntityListsParams,
  useGetEntityListsQuery,
} from '../../../../ProjectAdministration/EntityListAdministration/apiEntityLists';
import {
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../../../apis/apiGlobalConfiguration';
import useSpecialOptionsBasedOnConfiguration from './useSpecialOptionsBasedOnConfiguration';
import { isRootCategory } from '../../../../../lib/categories/categoriesLib';
import { useEntityListSectionModifier } from './useEntityListSectionModifier';
import EntityListSelectOption from './EntityListSelectOption';
import { ROOT_CATEGORY_ID } from '../../../../../components/global/_constants';
import { useStableComputedValue } from '../../../../../hooks/useStableComputedValue';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';

interface ConfigurationEntityListMainRowProps {
  definition: CategoryConfigurationDefinitionResponse;
  forcedTitle?: string;
  forcedCategoryId?: number;
  /* When false, only the selected entity lists with enable/disable switch will be rendered, no selection control to add a new entity list will be rendered. Default: true */
  fullyFeatured?: boolean;
  disabled?: boolean;
}

type EntityListId = number;

const ConfigurationEntityListMainRow: React.FC<ConfigurationEntityListMainRowProps> = ({
  definition,
  forcedTitle,
  forcedCategoryId,
  fullyFeatured = true,
  disabled = false,
}) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { type, id: templateOrCalculationId } = templateOrCalculation;
  const selectedCategoryId = useCategoryId();
  const categoryId = forcedCategoryId ?? selectedCategoryId;
  const { rootCategoryConfiguration } = useCategoryConfiguration(categoryId);
  const [patchConfiguration, patchConfigurationStatus] = usePatchCategoryConfigurationMutation();
  const [patchCalculationConfiguration, patchCalculationConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();
  const [enableEntityListSection, disableEntityListSection] = useEntityListSectionModifier();

  const specialOptionsBasedOnConfiguration = useSpecialOptionsBasedOnConfiguration(
    definition.fieldType,
  );

  // Needed for the Target Request List setting
  const { data: calculationConfiguration, isFetching: calculationConfigurationFetching } =
    useGetCalculationConfigurationQuery(templateOrCalculation.value);

  const { data: entityLists, isFetching: entityListsFetching } = useGetEntityListsQuery(
    getDefaultEntityListsParams(),
  );

  const s = rtkQueryPatchStatusToConfigurationFieldStatus(patchConfigurationStatus);
  const status =
    s === ConfigurationFieldStatus.DEFAULT || s === ConfigurationFieldStatus.SUCCESS
      ? rtkQueryPatchStatusToConfigurationFieldStatus(patchCalculationConfigurationStatus)
      : s;

  const notConfiguredEntityLists = useStableComputedValue(
    () => {
      if (rootCategoryConfiguration && entityLists && calculationConfiguration) {
        return getAllAvailableEntityLists(
          definition.fieldType,
          rootCategoryConfiguration,
          entityLists,
          calculationConfiguration,
        );
      } else {
        return [];
      }
    },
    entityListsFetching || calculationConfigurationFetching,
    [definition.fieldType, entityLists, rootCategoryConfiguration, calculationConfiguration],
  );

  const listOptions: DefaultOptionType[] = useMemo(() => {
    return [
      {
        id: -1,
        label: 'Add list',
        value: -1,
      },
      ...specialOptionsBasedOnConfiguration.selectOptions,
      ...notConfiguredEntityLists.map(option => ({
        id: option.entityListId,
        label: <EntityListSelectOption entityList={option} />,
        value: option.entityListId,
      })),
    ];
  }, [specialOptionsBasedOnConfiguration.selectOptions, notConfiguredEntityLists]);

  //TODO: check for "Target" settings => SkuTypes can be problem
  const changeHandler = useCallback(
    (value: EntityListId | ConfigurationFieldType) => {
      if (!rootCategoryConfiguration) return;
      if (!isRootCategory(categoryId)) return;

      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        /*
        - in case the value is a number, we got "EntityListId"
        - otherwise, we got special option => ConfigurationFieldType, that is displayed in EL select options
       */
        const isNumber = typeof value === 'number';

        const configRoot = rootCategoryConfiguration[isNumber ? definition.fieldType : value];
        const payloadRoot: FieldConfigurationRequest[] = [];
        const isTarget = isTargetConfiguration(definition.sectionType);

        iterableSkuTypes.forEach(skuType => {
          if (isTarget && skuType !== SkuType.FastMover) return;
          if (isNumber) {
            const jsonRoot = parseEntityListConfigs(configRoot[skuType]);
            jsonRoot.push(createEntityListConfig(value, isRootCategory(categoryId) ? 1 : 0));

            payloadRoot.push({
              configurationFieldType: definition.fieldType,
              value: JSON.stringify(jsonRoot),
              skuType: isTarget ? undefined : skuType,
            });
          } else {
            patchCalculationConfiguration({
              // @ts-expect-error -- value is typeguarded above, but typescript doesn't seem to understand that
              templateOrCalculation: templateOrCalculation.value,
              request: {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- special configs should only be from SkuListConfigurationFlag
                skuListConfigurationFlagIds: value as unknown as SkuListConfigurationFlag,
              },
            });

            payloadRoot.push({
              configurationFieldType: value,
              value: '1',
              skuType: isTarget ? undefined : skuType,
            });
          }
        });

        if (payloadRoot.length > 0) {
          disableEntityListSection(templateOrCalculationId, categoryId, definition.fieldType);

          patchConfiguration({
            templateId: templateOrCalculation.id,
            categoryId: ROOT_CATEGORY_ID,
            payload: payloadRoot,
          }).finally(() => {
            enableEntityListSection(templateOrCalculationId, categoryId, definition.fieldType);
          });
        }
      }
    },
    [
      rootCategoryConfiguration,
      categoryId,
      definition.fieldType,
      definition.sectionType,
      patchCalculationConfiguration,
      templateOrCalculation,
      disableEntityListSection,
      templateOrCalculationId,
      patchConfiguration,
      enableEntityListSection,
    ],
  );

  const fieldsDisabled = disabled || !isRootCategory(categoryId);

  const title = forcedTitle ?? definition.title;

  return (
    <ConfigurationRowWrapper>
      <ConfigurationRowTitle
        definition={definition}
        forcedTitle={title}
        fullyFeatured={fullyFeatured}
        highlighted={highlightedConfigurationFieldTypes[definition.fieldType]}
        disabled={fieldsDisabled}
      />
      {isSectionTypeSource(definition.sectionType) && type !== 'Calculation' && (
        <ConfigurationFieldsWrapperCentered>
          {fullyFeatured && (
            <Row $padding={1} $width="100%">
              <ConfigurationEntityListSelect
                options={listOptions}
                onChange={changeHandler}
                disabled={status === ConfigurationFieldStatus.LOADING || fieldsDisabled}
              />
            </Row>
          )}
        </ConfigurationFieldsWrapperCentered>
      )}
      {isSectionTypeTarget(definition.sectionType) && type !== 'Calculation' && (
        <ConfigurationFieldsWrapperTarget>
          {fullyFeatured && (
            <Row $padding={1} $width="100%" $justifyContent="center">
              <ConfigurationEntityListSelect
                options={listOptions}
                onChange={changeHandler}
                disabled={status === ConfigurationFieldStatus.LOADING || fieldsDisabled}
                width={computeRemSize(150)}
              />
            </Row>
          )}
        </ConfigurationFieldsWrapperTarget>
      )}
    </ConfigurationRowWrapper>
  );
};

export default ConfigurationEntityListMainRow;
